import { z } from 'zod';

import { type Duration, DurationUnits, convertDurationToMilliseconds } from './baseSchema.js';
import { BlockValueNumberSchema } from './blockValueNumberSchema.js';
import { isDefined } from '../../../utils.js';

interface BlockValueTemplateDurationSchemaArgs {
  pathName?: string;
  minDuration?: Duration;
  maxDuration?: Duration;
}

export const BlockValueTemplateDurationBaseSchema = z.object({
  value: BlockValueNumberSchema({ numberValidationSchema: z.number().gte(0) }),
  units: z.nativeEnum(DurationUnits),
});

export type BlockValueTemplateDuration = z.infer<typeof BlockValueTemplateDurationBaseSchema>;

export const BlockValueTemplateDurationSchema = (
  props: BlockValueTemplateDurationSchemaArgs = {}
) => {
  const { pathName, minDuration, maxDuration } = props;

  if (!isDefined(minDuration) && !isDefined(maxDuration)) {
    return BlockValueTemplateDurationBaseSchema;
  }

  // The args type guarantees pathName is set alongside any bound; narrow it for
  // the compiler (TypeScript can't correlate it from the min/max check alone).
  if (!isDefined(pathName)) {
    throw new Error('BlockValueTemplateDurationSchema: pathName is required when a bound is set');
  }

  return BlockValueTemplateDurationBaseSchema.superRefine((duration, context) => {
    const numberValue = Number(duration.value);
    // If the value is not a number, it'll be a block value (e.g. {{block.data}})
    if (Number.isNaN(numberValue)) {
      return;
    }
    const milliseconds = convertDurationToMilliseconds({
      ...duration,
      value: numberValue,
    });
    if (isDefined(minDuration) && milliseconds < convertDurationToMilliseconds(minDuration)) {
      return context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The minimum allowed for ${pathName} is ${minDuration.value} ${minDuration.units}.`,
        path: [pathName],
      });
    }
    if (isDefined(maxDuration) && milliseconds > convertDurationToMilliseconds(maxDuration)) {
      return context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The maximum allowed for ${pathName} is ${maxDuration.value} ${maxDuration.units}.`,
        path: [pathName],
      });
    }
  });
};
